import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { requestMobile } from '@/lib/request'
import { toast } from 'sonner'

interface BankCard {
  id: string
  bank_account: string
  bank_name: string
  real_name: string
  bank_code: string
  dv?: boolean
}

interface WithdrawInfo {
  usdt_withdraw_exchange_rate: number
}

interface WithdrawDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  accountInfo: {
    plat: string
    remarks_name: string
  }
  onSuccess?: () => void
}

export function WithdrawDialog({ open, onOpenChange, accountInfo, onSuccess }: WithdrawDialogProps) {
  const [loading, setLoading] = useState(false)
  const [bankCards, setBankCards] = useState<BankCard[]>([])
  const [selectedCardAccount, setSelectedCardAccount] = useState('')
  const [amount, setAmount] = useState('')
  const [withdrawInfo, setWithdrawInfo] = useState<WithdrawInfo | null>(null)

  // 当对话框打开时加载银行卡列表
  useEffect(() => {
    if (open) {
      loadBankCards()
    } else {
      resetDialog()
    }
  }, [open])

  const loadBankCards = async () => {
    try {
      const resp = await requestMobile('/common/apis/v1/bind-card-list', {
        plat: accountInfo.plat,
        name: accountInfo.remarks_name
      })

      if (resp && resp.data) {
        const cards = resp.data.resultList || []
        const info = resp.data.rspWithdrawInfo

        if (cards.length === 0) {
          toast.error('没有绑定银行卡')
          onOpenChange(false)
          return
        }

        setBankCards(cards)
        setWithdrawInfo(info)
      }
    } catch (error) {
      console.error('Failed to load bank cards:', error)
      toast.error('加载银行卡列表失败')
    }
  }

  const handleCardSelect = async (bankAccount: string) => {
    setSelectedCardAccount(bankAccount)

    const card = bankCards.find(c => c.bank_account === bankAccount)
    if (card && !card.dv) {
      // 设置为默认卡
      try {
        await requestMobile('/common/apis/v1/bind-card-set', { id: card.id })
      } catch (error) {
        console.error('Failed to set default card:', error)
      }
    }
  }

  const handleWithdraw = async () => {
    if (loading) return

    if (!selectedCardAccount) {
      toast.error('请选择银行卡')
      return
    }

    if (!amount || parseFloat(amount) <= 0) {
      toast.error('请输入正确的金额')
      return
    }

    const selectedCard = bankCards.find(c => c.bank_account === selectedCardAccount)
    if (!selectedCard) {
      toast.error('银行卡信息错误')
      return
    }

    setLoading(true)
    try {
      // 计算汇率信息
      const usdt_withdraw_exchange_rate = withdrawInfo?.usdt_withdraw_exchange_rate || 1
      const u_count = (parseFloat(amount) / usdt_withdraw_exchange_rate).toFixed(2)
      const bank_address = `${usdt_withdraw_exchange_rate}:${u_count}`

      const param = {
        bank_account: selectedCard.bank_account,
        bank_account_name: selectedCard.real_name,
        bank_address: bank_address,
        bank_name: selectedCard.bank_name,
        bank_code: selectedCard.bank_code,
        withdraw_money: parseInt(amount),
        plat: accountInfo.plat,
        name: accountInfo.remarks_name,
        type: 1
      }

      const resp = await requestMobile('/common/apis/v1/bank-card-withdraw', param)

      if (resp && resp.code === 0) {
        toast.success('提交成功')
        onOpenChange(false)
        resetDialog()
        onSuccess?.()
      }
    } catch (error) {
      console.error('Failed to withdraw:', error)
      toast.error('提现失败')
    } finally {
      setLoading(false)
    }
  }

  const resetDialog = () => {
    setSelectedCardAccount('')
    setAmount('')
    setBankCards([])
    setWithdrawInfo(null)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>提现</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* 银行卡选择 */}
          <div className="space-y-2">
            <Label>选择银行卡</Label>
            <RadioGroup value={selectedCardAccount} onValueChange={handleCardSelect}>
              <div className="space-y-2">
                {bankCards.map((card) => (
                  <div key={card.bank_account} className="relative">
                    <RadioGroupItem
                      value={card.bank_account}
                      id={`card-${card.bank_account}`}
                      className="peer sr-only"
                    />
                    <Label
                      htmlFor={`card-${card.bank_account}`}
                      className="flex flex-col gap-1 rounded-lg border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                    >
                      <div className="font-medium">{card.bank_account}</div>
                      <div className="text-sm text-muted-foreground">
                        {card.bank_name} - {card.real_name}
                      </div>
                    </Label>
                  </div>
                ))}
              </div>
            </RadioGroup>
          </div>

          {/* 金额输入 */}
          <div className="space-y-2">
            <Label htmlFor="amount">金额</Label>
            <Input
              id="amount"
              type="number"
              placeholder="请输入金额"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>

          {/* 汇率提示 */}
          {withdrawInfo && (
            <div className="text-sm text-muted-foreground">
              当前汇率: {withdrawInfo.usdt_withdraw_exchange_rate}
            </div>
          )}

          {/* 操作按钮 */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              取消
            </Button>
            <Button
              onClick={handleWithdraw}
              disabled={loading || !selectedCardAccount || !amount}
              className="flex-1"
            >
              {loading ? '提交中...' : '确认提现'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
